import { createContext, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import { Backdrop, CircularProgress, CssBaseline, Fade } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Box } from "@mui/system";
import { SnackbarProvider } from "notistack";

import {
  PlacementCards,
  ExternalRoute,
  SiteFooter,
  Layout,
  TestReviewSection,
} from "./components";

import {
  Signin,
  Dashboard,
  Tests,
  Exercises,
  ProgressReport,
  InstructionsPage,
  UpdateDetails,
  Question,
  StudentManagement,
  StudentDetailManagement,
  TestManagement,
  TestDetailManagement,
  StudentTraining,
  TrainingManagement,
  TrainingDetailManagement,
  ReportManagement,
  ReportDetailManagement,
  ClassManagement,
  ClassDetailManagement,
  ClassManagementTeacher,
  IndividualStudentMonitor,
  CourseManagement,
  CourseDetailManagement,
  ProgramManagement,
  ProgramDetailManagement,
  Payment,
  PlacementReport,
  UserManagement,
  Curriculum,
  ClassTrainingView,
  PostAssessmentReport,
  ProgramAssessments,
} from "./pages";

import { useLazyQuery, useMutation } from "@apollo/client";

import { LicenseInfo } from "@mui/x-license-pro";

import {
  CREATE_SITTING,
  SAVE_ANSWER,
  FINISH_SITTING,
  UPDATE_SITTING,
  DELETE_TRAINING_ANSWERS,
  DELETE_SITTING,
  CHANGE_QUESTION,
} from "./graphql/mutations";
import {
  GET_FORMATTED_QUESTION,
  GET_FORMATTED_PASSAGE,
  GET_FORMATTED_DIAG_QUESTION,
  GET_ROLE,
} from "./graphql/queries";

import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
export const AuthenticatedContext = createContext({});
export const ModuleContext = createContext({});
export const TimerContext = createContext({});
export const PreferenceContext = createContext({});
export const StudentMonitoringContext = createContext({});
var moment = require("moment");

LicenseInfo.setLicenseKey(
  "db20bdb6213a77575d2d6fb8b7c527cbTz0xMDY4MzUsRT0xNzcwNTk1MTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1pbml0aWFsLEtWPTI="
);
const theme = createTheme({
  palette: {
    primary: {
      main: "#213162",
    },
    secondary: {
      main: "#abd7fe",
    },
    success: {
      main: "#22D160",
      contrastText: "#fff",
    },
  },
  typography: {
    fontFamily: ["Poppins", "sans-serif"].join(","),
  },
});

function App() {
  const [defaults] = useState({
    lastUpdate: "11/12/2024",
    studentManagement: {
      widths: {
        actions: 150,
        login: 210,
        name: 400,
        campus: 200,
        gradeId: 220,
        isActivated: 120,
        createdAt: 190,
      },
      pageSize: 10,
      columns: [
        "id",
        "actions",
        "login",
        "name",
        "campus",
        "gradeId",
        "isActivated",
        "createdAt",
      ],
      visible: {
        id: false,
        actions: true,
        login: true,
        name: true,
        campus: true,
        isActivated: true,
        gradeId: true,
        createdAt: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
        quickFilterExcludeHiddenColumns: true,
      },
      sort: [{ field: "id", sort: "asc" }],
    },
    reportManagement: {
      widths: {
        actions: 150,
        name: 400,
        reportType: 220,
        isViewable: 120,
        createdAt: 190,
        updatedAt: 190,
      },
      pageSize: 10,
      columns: [
        "id",
        "actions",
        "name",
        "reportType",
        "isViewable",
        "createdAt",
        "updatedAt",
      ],
      visible: {
        id: false,
        actions: true,
        name: true,
        isViewable: true,
        reportType: true,
        createdAt: true,
        updatedAt: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
        quickFilterExcludeHiddenColumns: true,
      },
      sort: [{ field: "id", sort: "asc" }],
    },
    registrations: {
      widths: {
        id: 75,
        actions: 150,
        login: 220,
        name: 400,
        campus: 150,
        gradeId: 220,
        isCompleted: 220,
        createdAt: 190,
        hasPaid: 200,
      },
      pageSize: 10,
      columns: [
        "id",
        "actions",
        "login",
        "name",
        "campus",
        "gradeId",
        "hasPaid",
        "isCompleted",
        "createdAt",
      ],
      visible: {
        id: false,
        login: true,
        actions: true,
        name: true,
        campus: true,
        gradeId: true,
        isCompleted: true,
        createdAt: true,
        hasPaid: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
        quickFilterExcludeHiddenColumns: true,
      },
      sort: [{ field: "id", sort: "asc" }],
    },
    testReg: {
      widths: {
        actions: 150,
        id: 100,
        name: 400,
        isCompleted: 220,
        createdAt: 190,
      },
      pageSize: 10,
      columns: ["id", "actions", "name", "isCompleted", "createdAt"],
      visible: {
        id: true,
        actions: true,
        name: true,
        isCompleted: true,
        createdAt: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
      },
      sort: [{ field: "id", sort: "asc" }],
    },
    trainReg: {
      widths: {
        actions: 150,
        id: 100,
        name: 400,
        isCompleted: 400,
        createdAt: 190,
        course: 200,
      },
      pageSize: 10,
      columns: ["id", "actions", "name", "course", "isCompleted", "createdAt"],
      visible: {
        id: true,
        actions: true,
        name: true,
        isCompleted: true,
        createdAt: true,
        course: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
      },
      sort: [{ field: "id", sort: "asc" }],
    },
    sittings: {
      widths: {
        actions: 75,
        id: 100,
        studentId: 100,
        studentNumber: 150,
        name: 350,
        isCompleted: 200,
        upTo: 100,
        createdAt: 190,
        completedDate: 190,
        campus: 190,
      },
      pageSize: 10,
      columns: [
        "actions",
        "id",
        "studentId",
        "studentNumber",
        "name",
        "campus",
        "isCompleted",
        "upTo",
        "createdAt",
        "completedDate",
      ],
      visible: {
        id: true,
        actions: true,
        name: true,
        campus: true,
        isCompleted: true,
        createdAt: true,
        studentId: true,
        upTo: true,
        completedDate: true,
        studentNumber: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
        quickFilterExcludeHiddenColumns: true,
      },
      sort: [{ field: "id", sort: "asc" }],
    },
    testMgmt: {
      widths: {
        actions: 150,
        id: 100,
        name: 350,
        totalQuestions: 220,
        isAdaptive: 100,
        isActive: 100,
        availableFrom: 190,
        availableTo: 190,
        createdAt: 190,
        updatedAt: 190,
      },
      pageSize: 10,
      columns: [
        "actions",
        "id",
        "name",
        "totalQuestions",
        "isAdaptive",
        "isActive",
        "availableFrom",
        "availableTo",
        "createdAt",
        "updatedAt",
      ],
      visible: {
        actions: true,
        id: true,
        name: true,
        totalQuestions: true,
        isAdaptive: true,
        isActive: true,
        availableFrom: true,
        availableTo: true,
        createdAt: true,
        updatedAt: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
        quickFilterExcludeHiddenColumns: true,
      },
      sort: [{ field: "id", sort: "asc" }],
    },
    studentTraining: {
      widths: {
        actions: 460,
        id: 100,
        originId: 100,
        name: 300,
        type: 150,
        status: 150,
        progress: 120,
        completionDate: 150,
        dueDate: 150,
      },
      pageSize: 10,
      columns: [
        "actions",
        "id",
        "originId",
        "name",
        "type",
        "status",
        "progress",
        "completionDate",
        "dueDate",
      ],
      visible: {
        actions: true,
        id: false,
        originId: false,
        name: true,
        type: true,
        status: true,
        progress: true,
        completionDate: true,
        dueDate: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
        quickFilterExcludeHiddenColumns: true,
      },
      sort: [{ field: "id", sort: "asc" }],
    },
    classManagement: {
      widths: {
        actions: 125,
        id: 100,
        name: 200,
        course: 300,
        numOfStudents: 130,
        region: 100,
        centre: 200,
        createdBy: 200,
        updatedBy: 200,
      },
      pageSize: 10,
      columns: [
        "actions",
        "id",
        "name",
        "course",
        "region",
        "centre",
        "numOfStudents",
        "createdBy",
        "updatedBy",
      ],
      visible: {
        actions: true,
        id: false,
        name: true,
        course: true,
        numOfStudents: true,
        region: true,
        centre: true,
        createdBy: true,
        updatedBy: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
        quickFilterExcludeHiddenColumns: true,
      },
      sort: [{ field: "id", sort: "asc" }],
    },
    trainingMgmt: {
      widths: {
        actions: 150,
        id: 150,
        numEnrolled: 150,
        numActive: 150,
        name: 350,
        assessmentId: 150,
        totalQuestions: 180,
        firstGroupQuestions: 230,
        timeOfFirstGroupQuestions: 250,
        totalCorrectScoreSectionOne: 250,
        totalCorrectScoreSectionTwo: 250,
        isActive: 100,
        createdAt: 130,
        updatedAt: 130,
      },
      pageSize: 10,
      columns: [
        "id",
        "name",
        "numEnrolled",
        "numActive",
        "assessmentId",
        "totalQuestions",
        "firstGroupQuestions",
        "timeOfFirstGroupQuestions",
        "totalCorrectScoreSectionOne",
        "totalCorrectScoreSectionTwo",
        "isActive",
        "createdAt",
        "updatedAt",
      ],
      visible: {
        actions: true,
        id: true,
        numEnrolled: true,
        numActive: true,
        name: true,
        assessmentId: true,
        totalQuestions: true,
        firstGroupQuestions: true,
        timeOfFirstGroupQuestions: true,
        totalCorrectScoreSectionOne: true,
        totalCorrectScoreSectionTwo: true,
        isActive: true,
        createdAt: true,
        updatedAt: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
        quickFilterExcludeHiddenColumns: true,
      },
      sort: [{ field: "id", sort: "asc" }],
    },
    reportTest: {
      widths: {
        actions: 150,
        testId: 100,
        testName: 400,
        psType: 200,
        createdAt: 190,
        updatedAt: 190,
      },
      columns: [
        "testId",
        "actions",
        "testName",
        "psType",
        "createdAt",
        "updatedAt",
      ],
      visible: {
        testId: true,
        actions: true,
        testName: true,
        psType: true,
        createdAt: true,
        updatedAt: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
        quickFilterExcludeHiddenColumns: true,
      },
      sort: [{ field: "id", sort: "asc" }],
      pageSize: 10,
    },
    reportStudent: {
      widths: {
        actions: 80,
        studentNumber: 150,
        studentName: 400,
        campus: 200,
        countTest: 150,
        reports: 200,
        trainings: 200,
        isSent: 200,
        isRead: 200,
        createdAt: 190,
      },
      columns: [
        "studentNumber",
        "actions",
        "studentName",
        "campus",
        "countTest",
        "reports",
        "trainings",
        "isSent",
        "isRead",
        "createdAt",
      ],
      visible: {
        studentNumber: true,
        actions: true,
        studentName: true,
        campus: true,
        countTest: true,
        reports: true,
        trainings: true,
        createdAt: true,
        isSent: true,
        isRead: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
        quickFilterExcludeHiddenColumns: true,
      },
      sort: [{ field: "id", sort: "asc" }],
      pageSize: 10,
    },
    courseMgmt: {
      widths: {
        actions: 80,
        id: 80,
        name: 350,
        availableFrom: 300,
        availableTo: 300,
        createdAt: 190,
        updatedAt: 190,
      },
      columns: [
        "actions",
        "id",
        "name",
        "availableFrom",
        "availableTo",
        "createdAt",
        "updatedAt",
      ],
      visible: {
        actions: true,
        id: true,
        name: true,
        availableFrom: true,
        availableTo: true,
        createdAt: true,
        updatedAt: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
        quickFilterExcludeHiddenColumns: true,
      },
      sort: [{ field: "id", sort: "asc" }],
      pageSize: 10,
    },
    programMgmt: {
      widths: {
        actions: 80,
        id: 80,
        name: 350,
        availableFrom: 300,
        availableTo: 300,
        createdAt: 190,
        updatedAt: 190,
        course: 350,
        program: 350,
        hasDiagnostic: 190,
        isPost: 150,
      },
      columns: [
        "actions",
        "id",
        "name",
        "availableFrom",
        "availableTo",
        "course",
        "program",
        "hasDiagnostic",
        "createdAt",
        "updatedAt",
        "isPost",
      ],
      visible: {
        actions: true,
        id: true,
        name: true,
        availableFrom: true,
        availableTo: true,
        course: true,
        program: true,
        createdAt: true,
        updatedAt: true,
        hasDiagnostic: true,
        isPost: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
        quickFilterExcludeHiddenColumns: true,
      },
      sort: [{ field: "id", sort: "asc" }],
      pageSize: 10,
    },
    diagSittings: {
      widths: {
        actions: 75,
        id: 100,
        studentId: 100,
        studentNumber: 150,
        name: 350,
        isCompleted: 200,
        upTo: 100,
        createdAt: 190,
        completedDate: 190,
        campus: 190,
        route: 200,
      },
      pageSize: 10,
      columns: [
        "actions",
        "id",
        "studentId",
        "studentNumber",
        "name",
        "campus",
        "isCompleted",
        "upTo",
        "route",
        "createdAt",
        "completedDate",
      ],
      visible: {
        id: true,
        actions: true,
        name: true,
        campus: true,
        isCompleted: true,
        createdAt: true,
        studentId: true,
        upTo: true,
        completedDate: true,
        studentNumber: true,
        route: true,
      },
      filters: {
        items: [],
        linkOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
        quickFilterExcludeHiddenColumns: true,
      },
      sort: [{ field: "id", sort: "asc" }],
    },
  });
  const [isAuthenticated, setIsAuthenticated] = useState(
    sessionStorage.getItem("auth") === "true"
  );
  const [userToken, setUserToken] = useState(sessionStorage.getItem("token"));
  const [userRole, setUserRole] = useState("");
  const [user, setUser] = useState(sessionStorage.getItem("user") || "");
  const [loading, setLoading] = useState(false);
  const [placementsDone, setPlacementsDone] = useState(
    sessionStorage.getItem("placementsDone") === "true"
  );
  const [studentMonitoringData, setStudentMonitoringData] = useState(
    JSON.parse(sessionStorage.getItem("studentMonitoringData")) || null
  );

  const [moduleSettings, setModuleSettings] = useState(
    JSON.parse(sessionStorage.getItem("moduleSettings")) || {
      selectedModule: null,
      moduleType: "",
      currentSitting: null,
      currentQuestion: null,
      answers: [],
      flags: [],
      reviews: [],
      questions: [],
      reviewQuestions: [],
      marks: null,
      currentQuestionNumber: 0,
      zetas: null,
      route: [],
      abilities: null,
      totalQuestionNumber: 0,
      questionsPicked: null,
      strands: [],
      passages: [],
      isPassage: false,
      passageLeft: 0,
      isComprehension: false,
      numStart: 0,
      loadedPassages: {},
      selectedAnswer: "",
      open: false,
      openReview: false,
      initialAnswer: "",
      startTime: null,
      endTime: null,
      totalLogoutTime: null,
      countdown: false,
      incorrect: [],
      incorrectIndex: 0,
      diagTree: [],
      attempt: null,
      maxOrder: 1,
      isTrainingAnswerCorrect: false,
      isAssessmentAnswerCorrect: false,
      isResultShown: false,
      readOnly: false,
      currentPart: 1,
      partOneAllCorrect: false,
      sectionOnePass: false,
      sectionTwoPass: false,
      totalScorePartOne: "",
      totalScorePartTwo: "",
      totalScorePartThree: "",
      totalTimeTakenPartOne: 0,
      totalTimeTakenPartTwo: 0,
      totalTimeTakenPartThree: 0,
      lowest: [],
      limit: {},
      openDiag: false,
      totalParts: 0,
    }
  );

  const [preferences, setPreferences] = useState({});

  const [timeLeft, setTimeLeft] = useState(0);
  const [timeUp, setTimeUp] = useState(false);
  const timeSettings = {
    timeLeft,
    setTimeLeft,
    timeUp,
    setTimeUp,
  };

  const exitSitting = () => {
    setTimeUp(false);
    setModuleSettings({
      selectedModule: null,
      moduleType: "",
      currentSitting: null,
      currentQuestion: null,
      answers: [],
      flags: [],
      reviews: [],
      questions: [],
      reviewQuestions: [],
      marks: null,
      currentQuestionNumber: null,
      zetas: null,
      route: [],
      abilities: null,
      totalQuestionNumber: null,
      questionsPicked: null,
      strands: [],
      passages: [],
      isPassage: false,
      passageLeft: 0,
      isComprehension: false,
      numStart: 0,
      loadedPassages: {},
      selectedAnswer: "",
      open: false,
      openReview: false,
      initialAnswer: "",
      startTime: null,
      endTime: null,
      totalLogoutTime: null,
      countdown: false,
      incorrect: [],
      incorrectIndex: 0,
      diagTree: [],
      attempt: null,
      maxOrder: 1,
      isTrainingAnswerCorrect: false,
      isAssessmentAnswerCorrect: false,
      isResultShown: false,
      readOnly: false,
      currentPart: 1,
      partOneAllCorrect: false,
      sectionOnePass: false,
      sectionTwoPass: false,
      totalScorePartOne: "",
      totalScorePartTwo: "",
      totalScorePartThree: "",
      totalTimeTakenPartOne: 0,
      totalTimeTakenPartTwo: 0,
      totalTimeTakenPartThree: 0,
      lowest: [],
      limit: {},
      openDiag: false,
      totalParts: 0,
    });
  };

  const loginSuccess = (role, user, token) => {
    setIsAuthenticated(true);
    setUserRole(role);
    setUser(JSON.stringify(user));
    setUserToken(token);
  };

  const logoutSuccess = () => {
    setIsAuthenticated(false);
    setUserRole("");
    setUser("");
    setUserToken(null);
    setStudentMonitoringData(null);
    exitSitting();
  };

  const [getRole] = useLazyQuery(GET_ROLE, {
    fetchPolicy: "no-cache",
    onCompleted: ({ getRole }) => {
      setUserRole(getRole);
    },
  });

  const globalSettings = {
    isAuthenticated,
    userRole,
    user,
    userToken,
    loading,
    placementsDone,
    loginSuccess,
    logoutSuccess,
    setLoading,
    setPlacementsDone,
    getRole,
  };

  const [createSitting] = useMutation(CREATE_SITTING, {
    onError: (error) => {
      console.log(`create sitting error: ${error}`);
    },
    fetchPolicy: "network-only",
  });

  const [deleteSitting] = useMutation(DELETE_SITTING, {
    onError: (error) => {
      console.log(`delete sitting error: ${error}`);
    },
    fetchPolicy: "network-only",
  });

  const [saveAnswer] = useMutation(SAVE_ANSWER, {
    onError: (error) => {
      console.log(`save answer error: ${error}`);
    },
    fetchPolicy: "network-only",
  });

  const [getFormattedQuestion] = useLazyQuery(GET_FORMATTED_QUESTION, {
    fetchPolicy: "no-cache",
  });

  const [getFormattedPassage] = useLazyQuery(GET_FORMATTED_PASSAGE, {
    fetchPolicy: "no-cache",
  });

  const [finishSitting] = useMutation(FINISH_SITTING, {
    onError: (error) => {
      console.log(`finish sitting error: ${error}`);
    },
    fetchPolicy: "network-only",
  });

  const [updateSitting] = useMutation(UPDATE_SITTING, {
    onError: (error) => {
      console.log(`update sitting error: ${error}`);
    },
    fetchPolicy: "network-only",
  });

  const [getFormattedDiagQuestion] = useLazyQuery(GET_FORMATTED_DIAG_QUESTION, {
    fetchPolicy: "no-cache",
  });

  const [deleteTrainingAnswers] = useMutation(DELETE_TRAINING_ANSWERS, {
    onError: (error) => {
      console.log(`delete training answers error: ${error}`);
    },
    fetchPolicy: "network-only",
  });

  const [changeQuestion] = useMutation(CHANGE_QUESTION, {
    onError: (error) => {
      console.log(`change questions error: ${error}`);
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (
      moduleSettings.moduleType !== "Training" &&
      moduleSettings.currentSitting?.createdAt &&
      moduleSettings.selectedModule?.duration
    ) {
      setTimeLeft(
        moduleSettings.currentSitting?.createdAt +
          moduleSettings.selectedModule?.duration * 60 * 1000
      );
    }
  }, [moduleSettings.currentSitting, moduleSettings.selectedModule]);

  useEffect(() => {
    if (
      moduleSettings.selectedModule &&
      moduleSettings.currentSitting &&
      moduleSettings.moduleType === "Training"
    ) {
      if (moduleSettings.totalLogoutTime !== null) {
        setTimeLeft(
          moduleSettings.currentSitting?.createdAt +
            moduleSettings.selectedModule?.timeOfFirstGroupQuestions *
              60 *
              1000 +
            moduleSettings.totalLogoutTime
        );
      } else {
        setTimeLeft(
          moduleSettings.currentSitting?.createdAt +
            moduleSettings.selectedModule?.timeOfFirstGroupQuestions * 60 * 1000
        );
      }
    }
  }, [moduleSettings.selectedModule, moduleSettings.currentSitting]);

  useEffect(() => {
    sessionStorage.setItem("placementsDone", placementsDone);
  }, [placementsDone]);

  const moduleFunctions = {
    createSitting: async (id, type) => {
      if (moduleSettings.moduleType === "Diagnostic") {
        const result = await createSitting({
          variables: {
            program_id: moduleSettings.selectedModule.id,
            module_type: moduleSettings.moduleType,
          },
        });
        if (result && result.data) return;
      } else {
        const result = await createSitting({
          variables: {
            module_id: id || moduleSettings.selectedModule.id,
            module_type: type || moduleSettings.moduleType,
          },
        });
        if (result && result.data) return;
      }
    },
    mfSaveAnswer: async (attempt) => {
      let answer;
      if (moduleSettings.moduleType === "Diagnostic") {
        answer = moduleSettings.answers.find(
          (a) => a.diagnosticQuestionId === moduleSettings.currentQuestion.id
        );
      } else if (moduleSettings.moduleType === "Training") {
        answer = moduleSettings.answers.find(
          (a) => a.questionNumber === moduleSettings.currentQuestion.order
        );
      } else {
        answer = moduleSettings.answers.find(
          (a) => a.questionId === moduleSettings.currentQuestion.id
        );
      }

      let result;
      let timeTaken = moduleSettings.startTime
        ? moment() - moment(moduleSettings.startTime)
        : null;
      let timeTakenTraining =
        moduleSettings.startTime && moduleSettings.endTime
          ? moment(moduleSettings.endTime) - moment(moduleSettings.startTime)
          : null;
      let timeReviewingTakenTraining = moduleSettings.endTime
        ? moment() - moment(moduleSettings.endTime)
        : null;

      if (answer) {
        if (
          moduleSettings.moduleType === "Diagnostic" ||
          (moduleSettings.initialAnswer === "" &&
            moduleSettings.selectedAnswer === "") ||
          moduleSettings.initialAnswer !== moduleSettings.selectedAnswer
        ) {
          result = await saveAnswer({
            variables: {
              sitting_id: moduleSettings.currentSitting.id,
              module_type: moduleSettings.moduleType,
              question_id: moduleSettings.currentQuestion.id,
              question_number: moduleSettings.currentQuestionNumber,
              is_flagged: !!moduleSettings.flags.find(
                (id) => id === moduleSettings.currentQuestion.id
              ),
              working_time:
                moduleSettings.moduleType === "Training" &&
                !moduleSettings.isResultShown &&
                !moduleSettings.readOnly
                  ? timeTakenTraining
                  : timeTaken,
              reviewing_time:
                moduleSettings.moduleType === "Training" &&
                moduleSettings.isResultShown &&
                moduleSettings.readOnly
                  ? timeReviewingTakenTraining
                  : 0,
              chosen: moduleSettings.selectedAnswer,
              answer_id: answer.id,
              current_section: moduleSettings.currentPart,
              is_result_shown:
                moduleSettings.isResultShown === true ? true : false,
              read_only: moduleSettings.readOnly === true ? true : false,
              attempt:
                attempt &&
                moduleSettings.moduleType === "Assessment" &&
                attempt,
            },
          });
        } else {
          result = await saveAnswer({
            variables: {
              sitting_id: moduleSettings.currentSitting.id,
              module_type: moduleSettings.moduleType,
              question_id: moduleSettings.currentQuestion.id,
              question_number: moduleSettings.currentQuestionNumber,
              is_flagged: !!moduleSettings.flags.find(
                (id) => id === moduleSettings.currentQuestion.id
              ),
              working_time:
                moduleSettings.moduleType === "Training" &&
                !moduleSettings.isResultShown &&
                !moduleSettings.readOnly
                  ? timeTakenTraining
                  : 0,
              reviewing_time:
                moduleSettings.moduleType === "Training" &&
                moduleSettings.isResultShown &&
                moduleSettings.readOnly
                  ? timeReviewingTakenTraining
                  : timeTaken,
              chosen: moduleSettings.selectedAnswer,
              answer_id: answer.id,
              current_section: moduleSettings.currentPart,
              is_result_shown:
                moduleSettings.isResultShown === true ? true : false,
              read_only: moduleSettings.readOnly === true ? true : false,
              attempt:
                attempt &&
                moduleSettings.moduleType === "Assessment" &&
                attempt,
            },
          });
        }
      } else {
        result = await saveAnswer({
          variables: {
            sitting_id: moduleSettings.currentSitting.id,
            module_type: moduleSettings.moduleType,
            question_id: moduleSettings.currentQuestion.id,
            question_number: moduleSettings.currentQuestionNumber,
            is_flagged: !!moduleSettings.flags.find(
              (id) => id === moduleSettings.currentQuestion.id
            ),
            working_time:
              moduleSettings.moduleType === "Training" &&
              moduleSettings.isResultShown &&
              moduleSettings.readOnly
                ? timeTakenTraining
                : timeTaken,
            reviewing_time:
              moduleSettings.moduleType === "Training" &&
              moduleSettings.isResultShown &&
              moduleSettings.readOnly
                ? timeReviewingTakenTraining
                : 0,
            chosen: moduleSettings.selectedAnswer,
            current_section: moduleSettings.currentPart,
            is_result_shown:
              moduleSettings.isResultShown === true ? true : false,
            read_only: moduleSettings.readOnly === true ? true : false,
          },
        });
      }

      if (result && result.data) {
        if (
          moduleSettings.moduleType === "Placement" ||
          moduleSettings.moduleType === "Test" ||
          moduleSettings.moduleType === "Assessment"
        ) {
          return [...result.data.saveAnswer.testAnswer];
        } else if (moduleSettings.moduleType === "Diagnostic") {
          answer = {
            ...result.data.saveAnswer.diagAnswer,
            isCorrect: result.data.saveAnswer.diagPath,
          };
          let index = moduleSettings.answers.findIndex(
            (a) => a.questionNumber === answer.questionNumber
          );

          if (index >= 0) {
            let newAnswers = [...moduleSettings.answers];
            newAnswers.splice(index, 1, answer);
            return newAnswers;
          } else {
            return [...moduleSettings.answers, answer];
          }
        } else if (
          moduleSettings.moduleType === "Training" ||
          moduleSettings.moduleType === "Interactive"
        ) {
          return [...result.data.saveAnswer.trainingAnswer];
        }
      }
    },
    goToQuestion: async (questionNumber, isSkip, attempt) => {
      setLoading(true);
      if (moduleSettings.moduleType === "Diagnostic") {
        const answers = await moduleFunctions.mfSaveAnswer();
        let nextQuestion;
        let newMaxOrder = moduleSettings.maxOrder;
        let addedPassage = false;
        let newPassages = { ...moduleSettings.loadedPassages };
        if (
          moduleSettings.currentQuestionNumber ===
            moduleSettings.totalQuestionNumber &&
          questionNumber === moduleSettings.currentQuestionNumber + 1
        ) {
          let currTree = moduleSettings.diagTree.filter(
            (dt) =>
              dt.node === moduleSettings.currentQuestion.nodeId &&
              dt.originalQuestionId ===
                moduleSettings.currentQuestion.originalQuestionId
          );
          let questions = moduleSettings.questions.filter(
            (q) =>
              q.nodeId ===
                moduleSettings.diagTree.find(
                  (dt) =>
                    dt.parent === moduleSettings.currentQuestion.nodeId &&
                    dt.isCorrect === answers[answers.length - 1].isCorrect
                )?.node &&
              q.originalQuestionId ===
                moduleSettings.currentQuestion.originalQuestionId
          );

          if (
            questions.length === 0 ||
            (currTree[0].parent === "node-0" &&
              moduleSettings.currentSitting.correctIds
                ?.split(",")
                .findIndex(
                  (id) => id === currTree[0].originalQuestionId.toString()
                ) > -1) ||
            isSkip
          ) {
            let index = moduleSettings.currentSitting.route
              .split(",")
              .findIndex(
                (qId) =>
                  qId ===
                  moduleSettings.currentQuestion.originalQuestionId.toString()
              );
            if (
              index ===
              moduleSettings.currentSitting.route.split(",").length - 1
            ) {
              setModuleSettings({
                ...moduleSettings,
                openDiag: true,
              });
              setLoading(false);
              return;
            }
            let firstQuestion = moduleSettings.diagTree.find(
              (dt) =>
                dt.originalQuestionId ===
                  Number(
                    moduleSettings.currentSitting.route.split(",")[index + 1]
                  ) && dt.parent === "node-0"
            );
            const pickedQuestions = moduleSettings.questions.filter(
              (q) =>
                q.originalQuestionId ===
                  Number(
                    moduleSettings.currentSitting.route.split(",")[index + 1]
                  ) && q.nodeId === firstQuestion.node
            );
            if (pickedQuestions.length > 1) {
              nextQuestion = pickedQuestions.find((q) => q.order === 1);
            } else {
              nextQuestion = pickedQuestions[0];
            }
            newMaxOrder = 1;
          } else if (questions.length > 1) {
            nextQuestion = questions.find((q) => q.order === newMaxOrder);
            let answer = answers.find(
              (a) => a.diagnosticQuestionId === nextQuestion.id
            );
            if (answer) {
              newMaxOrder++;
              nextQuestion = questions.find((q) => q.order === newMaxOrder);
              if (!nextQuestion) {
                let index = moduleSettings.currentSitting.route
                  .split(",")
                  .findIndex(
                    (qId) =>
                      qId ===
                      moduleSettings.currentQuestion.originalQuestionId.toString()
                  );
                if (
                  index ===
                  moduleSettings.currentSitting.route.split(",").length - 1
                ) {
                  setModuleSettings({
                    ...moduleSettings,
                    openDiag: true,
                  });
                  setLoading(false);
                  return;
                }
                let firstQuestion = moduleSettings.diagTree.find(
                  (dt) =>
                    dt.originalQuestionId ===
                      Number(
                        moduleSettings.currentSitting.route.split(",")[
                          index + 1
                        ]
                      ) && dt.parent === "node-0"
                );
                const pickedQuestions = moduleSettings.questions.filter(
                  (q) =>
                    q.originalQuestionId ===
                      Number(
                        moduleSettings.currentSitting.route.split(",")[
                          index + 1
                        ]
                      ) && q.nodeId === firstQuestion.node
                );
                newMaxOrder = 1;
                if (pickedQuestions.length > 1) {
                  nextQuestion = pickedQuestions.find((q) => q.order === 1);
                } else {
                  nextQuestion = pickedQuestions[0];
                }
              }
            }
          } else {
            nextQuestion = questions[0];
          }
          const result = await getFormattedDiagQuestion({
            variables: { question_id: nextQuestion.id },
          });
          if (result && result.data.getFormattedDiagQuestion) {
            nextQuestion = result.data.getFormattedDiagQuestion;
            if (nextQuestion.passageId) {
              if (moduleSettings.loadedPassages[nextQuestion.passageId]) {
                nextQuestion.src =
                  moduleSettings.loadedPassages[nextQuestion.passageId];
              } else {
                const passageResult = await getFormattedPassage({
                  variables: { passage_id: nextQuestion.passageId },
                });
                if (passageResult && passageResult.data.getFormattedPassage) {
                  nextQuestion.src = passageResult.data.getFormattedPassage;
                  newPassages[nextQuestion.passageId] =
                    passageResult.data.getFormattedPassage;
                  addedPassage = true;
                }
              }
            }
          }
        }
        await updateSitting({
          variables: {
            module_type: moduleSettings.moduleType,
            sitting_id: moduleSettings.currentSitting.id,
            up_to: nextQuestion.id.toString(),
            time_taken: answers[answers.length - 1].workingTime,
          },
        });
        setModuleSettings({
          ...moduleSettings,
          currentQuestionNumber: questionNumber,
          currentQuestion: nextQuestion,
          selectedAnswer: "",
          answers: [...answers],
          totalQuestionNumber: questionNumber,
          initialAnswer: "",
          startTime: moment(),
          maxOrder: newMaxOrder,
          attempt: 1,
          ...(addedPassage ? { loadedPassages: { ...newPassages } } : {}),
        });
        setLoading(false);
        return;
      } else if (moduleSettings.reviews?.length > 0) {
        let newQuestions = [...moduleSettings.questions];
        let changedQuestions = false;
        let newPassages = { ...moduleSettings.loadedPassages };
        let addedPassage = false;
        let review = moduleSettings.reviews.find(
          (r) => r.buttonNumber === questionNumber
        );
        let nextQuestion = newQuestions.find((q) => q.id === review.questionId);
        if (!nextQuestion.content) {
          const result = await getFormattedQuestion({
            variables: { question_id: nextQuestion.id, is_review: true },
          });
          if (result && result.data.getFormattedQuestion) {
            nextQuestion = result.data.getFormattedQuestion;
            if (nextQuestion.passageId) {
              if (moduleSettings.loadedPassages[nextQuestion.passageId]) {
                nextQuestion.src =
                  moduleSettings.loadedPassages[nextQuestion.passageId];
              } else {
                const passageResult = await getFormattedPassage({
                  variables: { passage_id: nextQuestion.passageId },
                });
                if (passageResult && passageResult.data.getFormattedPassage) {
                  nextQuestion.src = passageResult.data.getFormattedPassage;
                  newPassages[nextQuestion.passageId] =
                    passageResult.data.getFormattedPassage;
                  addedPassage = true;
                }
              }
            }
            const newQuestionIndex = newQuestions.findIndex(
              (q) => q.id === nextQuestion.id
            );
            newQuestions.splice(newQuestionIndex, 1, nextQuestion);
            changedQuestions = true;
          }
        }
        setModuleSettings({
          ...moduleSettings,
          currentQuestion: nextQuestion,
          selectedAnswer: review.chosen,
          ...(changedQuestions ? { questions: newQuestions } : {}),
          ...(addedPassage ? { loadedPassages: newPassages } : {}),
          openReview: false,
          currentQuestionNumber: questionNumber,
          incorrectIndex: moduleSettings.incorrect.findIndex(
            (i) => i === questionNumber
          ),
        });
        setLoading(false);
        return;
      }
      let timeTaken = moduleSettings.startTime
        ? moment() - moment(moduleSettings.startTime)
        : null;
      
      let timeTakenTraining =
      moduleSettings.startTime && moduleSettings.endTime
        ? moment(moduleSettings.endTime) - moment(moduleSettings.startTime)
        : null;
      let timeReviewingTakenTraining = moduleSettings.endTime
        ? moment() - moment(moduleSettings.endTime)
        : null;

      let answerInput;
      if (moduleSettings.moduleType === "Training") {
        answerInput = {
          trainingAnswer: {
            studentTrainingSittingId: moduleSettings.currentSitting.id,
            questionId: moduleSettings.currentQuestion.id,
            questionNumber: moduleSettings.currentQuestionNumber,
            isFlagged: !!moduleSettings.flags.find(
              (id) => id === moduleSettings.currentQuestion.id
            ),
            chosen: moduleSettings.selectedAnswer,
            workingTime: timeTakenTraining,
            currentSection: moduleSettings.currentPart,
            isResultShown:
              moduleSettings.isResultShown === true ? true : false,
            readOnly: moduleSettings.readOnly === true ? true : false,
          }
        }
      } else {
        answerInput = {
          testAnswer: {
            studentTestSittingId: moduleSettings.currentSitting.id,
            questionId: moduleSettings.currentQuestion.id,
            questionNumber: moduleSettings.currentQuestionNumber,
            isFlagged: !!moduleSettings.flags.find(
              (id) => id === moduleSettings.currentQuestion.id
            ),
            chosen: moduleSettings.selectedAnswer,
            attempt:
              attempt &&
              moduleSettings.moduleType === "Assessment" &&
              attempt,
          }
        }
      }
      
      if (moduleSettings.moduleType !== "Training") {
        if (
          ((moduleSettings.initialAnswer === "" && moduleSettings.selectedAnswer === "") ||
          moduleSettings.initialAnswer !== moduleSettings.selectedAnswer) 
        ) {
          answerInput.testAnswer.workingTime = timeTaken;
        } else {
          answerInput.testAnswer.reviewingTime = timeTaken;
        }
      }

      const needNewAdaptiveQuestion = () => {
        return !!(!moduleSettings.currentSitting.isComplete && 
        moduleSettings.selectedModule.isAdaptive && 
        (questionNumber >= moduleSettings.route.length + 1));
      }

      const needLoadNewQuestion = () => {
        if (moduleSettings.selectedModule.isAdaptive) {
          if (questionNumber <= moduleSettings.route.length) {
            const nextQuestionId = moduleSettings.route[questionNumber - 1].id;
            const foundQuestion = moduleSettings.questions.find(
              (q) => q.id === nextQuestionId
            );
            return foundQuestion;
          } else {
            return null;
          }
        } else {
          if (moduleSettings.moduleType === "Training") {
            return moduleSettings.questions.find(q => q.order === questionNumber);
          } else {
            return moduleSettings.questions[questionNumber - 1];
          }
        }
      }

      const loadNewQuestion = !!!(needLoadNewQuestion()?.content);
      const result = await changeQuestion({
        variables: {
          sittingId: moduleSettings.currentSitting.id,
          moduleType: moduleSettings.moduleType,
          answer: answerInput,
          newQuestionNumber: questionNumber,
          newAdaptiveQuestion: needNewAdaptiveQuestion(),
          loadNewQuestion: loadNewQuestion,
        }
      });

      let existingQuestion = needLoadNewQuestion();
      let newAnswer = "";
      if (moduleSettings.moduleType !== "Training") {
        const answerIndex = result?.data.changeQuestion.testAnswers.findIndex(
          (a) => a.questionId === existingQuestion?.id
        );
  
        if (answerIndex >= 0) {
          newAnswer = result?.data.changeQuestion.testAnswers[answerIndex].chosen;
        } else {
          newAnswer = "";
        }
      }
      

      let newQuestion;
      if (result?.data?.changeQuestion?.question) {
        newQuestion = {...result.data.changeQuestion.question};
        if (result.data.changeQuestion.passage) {
          newQuestion.src = result.data.changeQuestion.passage;
        }
      }

      let newQuestions = [...moduleSettings.questions];
      const newQuestionIndex = newQuestions.findIndex(
        (q) => q.id === newQuestion?.id
      );

      if (newQuestionIndex > -1) newQuestions.splice(newQuestionIndex, 1, newQuestion);

      console.log(newQuestion);
      setModuleSettings({
        ...moduleSettings,
        currentQuestionNumber: questionNumber,
        selectedAnswer: newAnswer,
        ...(moduleSettings.moduleType === "Training" ? { 
          answers: [...result?.data.changeQuestion.trainingAnswers],
          endTime: null,
        openReview: false,
        isTrainingAnswerCorrect: false,
        isResultShown: false,
        readOnly: false,
        totalTimeTakenPartOne:
          questionNumber <=
          moduleSettings.selectedModule.firstGroupQuestions + 1
            ? result?.data.changeQuestion.trainingAnswers
                .map((a) => a.workingTime)
                .reduce((acc, curr) => acc + curr, 0)
            : moduleSettings.totalTimeTakenPartOne,
        totalTimeTakenPartTwo:
          questionNumber >
            moduleSettings.selectedModule.firstGroupQuestions + 1 &&
          questionNumber <= moduleSettings.selectedModule.totalQuestions + 1
            ? result?.data.changeQuestion.trainingAnswers
                .map((a) => a.workingTime)
                .reduce((acc, curr) => acc + curr, 0)
            : moduleSettings.totalTimeTakenPartTwo,
        totalTimeTakenPartThree:
          questionNumber > moduleSettings.selectedModule.totalQuestions + 1
            ? moduleSettings.partOneAllCorrect
              ? result?.data.changeQuestion.trainingAnswers
                  .slice(moduleSettings.selectedModule.firstGroupQuestions)
                  .map((a) => a.workingTime)
                  .reduce((acc, curr) => acc + curr, 0)
              : result?.data.changeQuestion.trainingAnswers
                  .slice(moduleSettings.selectedModule.totalQuestions)
                  .map((a) => a.workingTime)
                  .reduce((acc, curr) => acc + curr, 0)
            : moduleSettings.totalTimeTakenPartThree,
        totalScorePartOne:
          questionNumber <=
          moduleSettings.selectedModule.firstGroupQuestions + 1
            ? Math.ceil(
                (result?.data.changeQuestion.trainingAnswers.filter((a) => a.isCorrect).length * 100) /
                  moduleSettings.selectedModule.firstGroupQuestions
              )
            : moduleSettings.totalScorePartOne,
        totalScorePartTwo:
          questionNumber >
            moduleSettings.selectedModule.firstGroupQuestions + 1 &&
          questionNumber <= moduleSettings.selectedModule.totalQuestions + 1
            ? Math.ceil(
                (result?.data.changeQuestion.trainingAnswers
                  .slice(moduleSettings.selectedModule.firstGroupQuestions)
                  .filter((a) => a.isCorrect).length *
                  100) /
                  (moduleSettings.selectedModule.totalQuestions -
                    moduleSettings.selectedModule.firstGroupQuestions)
              )
            : moduleSettings.totalScorePartTwo,
        totalScorePartThree:
          questionNumber > moduleSettings.selectedModule.totalQuestions + 1
            ? moduleSettings.currentSitting.isRedo
              ? Math.ceil(
                  (result?.data.changeQuestion.trainingAnswers
                    .slice(moduleSettings.selectedModule.totalQuestions)
                    .filter((a) => a.isCorrect).length *
                    100) /
                    (moduleSettings.selectedModule.totalQuestions -
                      moduleSettings.selectedModule.firstGroupQuestions)
                )
              : moduleSettings.partOneAllCorrect
              ? Math.ceil(
                  (result?.data.changeQuestion.trainingAnswers
                    .slice(moduleSettings.selectedModule.firstGroupQuestions)
                    .filter((a) => a.isCorrect).length *
                    100) /
                    (moduleSettings.selectedModule.totalQuestions -
                      moduleSettings.selectedModule.firstGroupQuestions)
                )
              : Math.ceil(
                  (result?.data.changeQuestion.trainingAnswers
                    .slice(moduleSettings.selectedModule.totalQuestions)
                    .filter((a) => a.isCorrect).length *
                    100) /
                    (moduleSettings.selectedModule.totalQuestions -
                      moduleSettings.selectedModule.firstGroupQuestions)
                )
            : moduleSettings.totalScorePartThree,
        } : { 
          answers: [...result?.data.changeQuestion.testAnswers] 
        }),
        ...(!!newQuestion ? { questions: [...newQuestions], currentQuestion: {...newQuestion } } : { currentQuestion: {...existingQuestion} }),
        initialAnswer: newAnswer,
        startTime: moment(),
        openReview: false,
        ...(result?.data.changeQuestion.route ?  { route: [...result?.data.changeQuestion.route] } : {}),
      });

      setLoading(false);
      return;
    },
    finishSitting: async (module, type) => {
      if (module && type === "Interactive") {
        const result = await finishSitting({
          variables: {
            module_id: module.id,
            module_type: type,
            sitting_id: module.TrainingSittings[0].id,
          },
        });
        setLoading(false);
        if (result && result.data) return;
      }
      if (
        moduleSettings.moduleType === "Diagnostic" &&
        !moduleSettings.currentSitting.route
      ) {
        const result = await finishSitting({
          variables: {
            module_id: moduleSettings.selectedModule.id,
            module_type: moduleSettings.moduleType,
            sitting_id: moduleSettings.currentSitting?.id,
          },
        });
        if (result && result.data) return;
      }
      const answers = await moduleFunctions.mfSaveAnswer();
      let score;
      if (moduleSettings.moduleType === "Diagnostic") {
        const result = await finishSitting({
          variables: {
            module_id: moduleSettings.selectedModule.id,
            module_type: moduleSettings.moduleType,
            sitting_id: moduleSettings.currentSitting.id,
          },
        });
        if (result && result.data) return;
      } else if (moduleSettings.moduleType === "Training") {
        let numCorrect = moduleSettings.partOneAllCorrect
          ? answers
              .slice(moduleSettings.selectedModule.firstGroupQuestions)
              .filter((a) => a.isCorrect).length
          : answers
              .slice(moduleSettings.selectedModule.totalQuestions)
              .filter((a) => a.isCorrect).length;
        score = `${
          (numCorrect * 100) /
          (moduleSettings.selectedModule.totalQuestions -
            moduleSettings.selectedModule.firstGroupQuestions)
        }`;

        const result = await finishSitting({
          variables: {
            module_id: moduleSettings.selectedModule.id,
            module_type: moduleSettings.moduleType,
            sitting_id: moduleSettings.currentSitting.id,
            score,
          },
        });
        if (result && result.data) return;
      } else if (moduleSettings.moduleType === "Interactive") {
        const result = await finishSitting({
          variables: {
            module_id: moduleSettings.selectedModule.id,
            module_type: moduleSettings.moduleType,
            sitting_id: moduleSettings.currentSitting.id,
          },
        });
        if (result && result.data) return;
      } else {
        const result = await finishSitting({
          variables: {
            module_id: moduleSettings.selectedModule.id,
            module_type: moduleSettings.moduleType,
            sitting_id: moduleSettings.currentSitting.id,
          },
        });
        if (result && result.data) return;
      }
    },
    deleteTrainingAnswers,
    deleteSitting,
    updateSitting,
    exitSitting,
  };

  const moduleValues = {
    moduleSettings,
    ...moduleFunctions,
    setModuleSettings,
  };

  useEffect(() => {
    console.log(moduleSettings);
    sessionStorage.setItem("moduleSettings", JSON.stringify(moduleSettings));
  }, [moduleSettings]);

  useEffect(() => {
    if (Object.keys(preferences).length === 0) {
      const localPref = localStorage.getItem("preferences");
      if (localPref) {
        if (localPref.lastUpdate === "24/8/2023") {
          setPreferences({ ...JSON.parse(localPref) });
        } else {
          setPreferences({ ...defaults });
        }
      } else {
        setPreferences({ ...defaults });
      }
    } else {
      localStorage.setItem("preferences", JSON.stringify(preferences));
    }
  }, [preferences]);

  useEffect(() => {
    sessionStorage.setItem(
      "studentMonitoringData",
      JSON.stringify(studentMonitoringData)
    );
  }, [studentMonitoringData]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <AuthenticatedContext.Provider value={globalSettings}>
        <ModuleContext.Provider value={moduleValues}>
          <StudentMonitoringContext.Provider
            value={{ studentMonitoringData, setStudentMonitoringData }}
          >
            <TimerContext.Provider value={timeSettings}>
              <PreferenceContext.Provider
                value={{ preferences, setPreferences, defaults }}
              >
                <ThemeProvider theme={theme}>
                  <SnackbarProvider
                    maxSnack={3}
                    autoHideDuration={5000}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    TransitionComponent={Fade}
                  >
                    {loading && (
                      <Backdrop
                        sx={{
                          zIndex: (theme) => theme.zIndex.drawer + 10000,
                        }}
                        open={loading}
                      >
                        <CircularProgress />
                      </Backdrop>
                    )}

                    <Router>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100vh",
                          width: "100vw",
                          position: "relative",
                        }}
                      >
                        <Box flex={1} overflow="auto">
                          <CssBaseline />
                          {isAuthenticated ? (
                            <Switch>
                              <Route exact path="/payment">
                                <Payment />
                              </Route>
                              <Route exact path="/report/:program">
                                <PlacementReport />
                              </Route>
                              <Route exact path="/report/:program/:id">
                                <PlacementReport />
                              </Route>
                              <Route exact path="/postreport/:program">
                                <PostAssessmentReport />
                              </Route>
                              <Route exact path="/postreport/:program/:id">
                                <PostAssessmentReport />
                              </Route>
                              <Layout>
                                <Route exact path="/dashboard">
                                  <Dashboard />
                                </Route>
                                <Route exact path="/tests">
                                  <Tests />
                                </Route>
                                <Route exact path="/assessments">
                                  <PlacementCards />
                                </Route>
                                <Route
                                  exact
                                  path="/program-assessments/:programName"
                                >
                                  <ProgramAssessments />
                                </Route>

                                <Route exact path="/exercises">
                                  <Exercises />
                                </Route>
                                <Route exact path="/progressreport">
                                  <ProgressReport />
                                </Route>

                                <Route exact path="/instructions">
                                  <InstructionsPage />
                                </Route>
                                <Route exact path="/updatedetails">
                                  <UpdateDetails />
                                </Route>
                                <Route exact path="/usermanagement">
                                  <UserManagement />
                                </Route>
                                <Route exact path="/placement-question">
                                  {moduleSettings.openReview ? (
                                    <TestReviewSection />
                                  ) : (
                                    <Question />
                                  )}
                                </Route>
                                <Route exact path="/training-question">
                                  {moduleSettings.openReview ? (
                                    <TestReviewSection />
                                  ) : (
                                    <Question />
                                  )}
                                </Route>
                                <Route exact path="/assessment-question">
                                  {moduleSettings.openReview ? (
                                    <TestReviewSection />
                                  ) : (
                                    <Question />
                                  )}
                                </Route>
                                <Route exact path="/studentmanagement">
                                  <StudentManagement />
                                </Route>
                                <Route exact path="/studentmanagement/:id">
                                  <StudentDetailManagement />
                                </Route>
                                <Route exact path="/testmanagement">
                                  <TestManagement />
                                </Route>
                                <Route
                                  exact
                                  path="/testmanagement/:moduleTypeId/:id"
                                >
                                  <TestDetailManagement />
                                </Route>
                                <Route exact path="/trainingmanagement">
                                  <TrainingManagement />
                                </Route>
                                <Route exact path="/trainingmanagement/:id">
                                  <TrainingDetailManagement />
                                </Route>
                                <Route exact path="/reportmanagement">
                                  <ReportManagement />
                                </Route>
                                <Route exact path="/studenttraining">
                                  <StudentTraining />
                                </Route>
                                <Route exact path="/reportmanagement/:id">
                                  <ReportDetailManagement />
                                </Route>
                                <Route exact path="/classmanagement">
                                  <ClassManagement />
                                </Route>
                                <Route exact path="/classmanagementteacher">
                                  <ClassManagementTeacher />
                                </Route>
                                <Route exact path="/classmanagementteacher/:id">
                                  <IndividualStudentMonitor />
                                </Route>
                                <Route exact path="/classmanagement/:id">
                                  <ClassDetailManagement />
                                </Route>
                                <Route exact path="/coursemanagement">
                                  <CourseManagement />
                                </Route>
                                <Route exact path="/coursemanagement/:id">
                                  <CourseDetailManagement />
                                </Route>
                                <Route exact path="/programmanagement">
                                  <ProgramManagement />
                                </Route>
                                <Route exact path="/programmanagement/:id">
                                  <ProgramDetailManagement />
                                </Route>
                                <Route
                                  exact
                                  path="/classmanagementteacher/:classId/:trainingId"
                                >
                                  <ClassTrainingView />
                                </Route>

                                <Route exact path="/curriculum/:id">
                                  <Curriculum />
                                </Route>
                              </Layout>
                            </Switch>
                          ) : (
                            <ExternalRoute path="/payment" component={Payment} />
                          )}

                          <Route exact path="/">
                            {isAuthenticated ? (
                              <Redirect to="/dashboard" />
                            ) : (
                              <Signin />
                            )}
                          </Route>
                        </Box>
                        <SiteFooter />
                      </Box>
                    </Router>
                  </SnackbarProvider>
                </ThemeProvider>
              </PreferenceContext.Provider>
            </TimerContext.Provider>
          </StudentMonitoringContext.Provider>
        </ModuleContext.Provider>
      </AuthenticatedContext.Provider>
    </LocalizationProvider>
  );
}

export default App;
